"use client";

import { useState, useSyncExternalStore } from "react";
import { ToolCard } from "@/components/ToolCard";
import { filterTools, type ToolFilter } from "@/lib/tools";
import { cn } from "@/lib/utils";

const TABS: { value: ToolFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "pdf", label: "PDF" },
  { value: "image", label: "Image" },
];

function subscribeHash(onChange: () => void) {
  window.addEventListener("hashchange", onChange);
  return () => window.removeEventListener("hashchange", onChange);
}

function filterFromHash(hash: string): ToolFilter | null {
  if (hash === "#pdf-tools") return "pdf";
  if (hash === "#image-tools") return "image";
  if (hash === "#tools") return "all";
  return null;
}

/** Tool grid with All / PDF / Image tabs. The header's "PDF tools" and
 *  "Image tools" links (/#pdf-tools, /#image-tools) pre-select a tab. */
export function ToolGrid() {
  // Server and first render see "" so hydration matches; then the real hash.
  const hash = useSyncExternalStore(
    subscribeHash,
    () => window.location.hash,
    () => "",
  );
  // A tab click wins until the hash changes again.
  const [picked, setPicked] = useState<{ filter: ToolFilter; hash: string }>();
  const filter =
    picked && picked.hash === hash
      ? picked.filter
      : (filterFromHash(hash) ?? picked?.filter ?? "all");
  const tools = filterTools(filter);

  return (
    <section
      id="tools"
      aria-labelledby="tools-heading"
      className="flex scroll-mt-4 flex-col gap-7 pb-14 sm:pb-20"
    >
      <span id="pdf-tools" className="scroll-mt-4" aria-hidden="true" />
      <span id="image-tools" className="scroll-mt-4" aria-hidden="true" />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h2 id="tools-heading" className="text-3xl sm:text-[40px]">
          Pick a tool
        </h2>
        <div
          role="group"
          aria-label="Filter tools"
          className="flex gap-1.5 rounded-lg bg-sand p-1"
        >
          {TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              aria-pressed={filter === tab.value}
              onClick={() => setPicked({ filter: tab.value, hash })}
              className={cn(
                "min-h-11 rounded-[9px] px-[18px] text-[15px] text-ink",
                filter === tab.value
                  ? "bg-card font-semibold shadow-xs"
                  : "font-medium hover:bg-card/60",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,260px),1fr))] gap-4">
        {tools.map((tool) => (
          <li key={tool.slug} className="grid">
            <ToolCard tool={tool} />
          </li>
        ))}
      </ul>
    </section>
  );
}
